import classNames from "classnames";
import React, { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMedia } from "react-use";

import { Section } from "@/components/Section";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IActiveFilterData } from "@/data/filters";
import { Flex } from "@/eworkitComponents";

import { FiltersActiveFilters } from "./FiltersActiveFilters";
import { FiltersButtons } from "./FiltersButtons";
import { FiltersCollapsible } from "./FiltersCollapsible";

import "./_filters-section.scss";

interface IFiltersSectionProps {
  renderMainFilters: () => ReactNode;
  renderMoreFilters: () => ReactNode;
  activeFilters: IActiveFilterData[];
  onClearFilters?: () => void;
  hideClearFilters?: boolean;
  signature?: string;
  forceOpen?: boolean;
  className?: string;
  wasLastOfferVisible?: boolean;
}

export const FiltersSection: FC<IFiltersSectionProps> = ({
  renderMainFilters,
  renderMoreFilters,
  activeFilters,
  onClearFilters,
  hideClearFilters,
  signature,
  forceOpen,
  className,
  wasLastOfferVisible,
}) => {
  const location = useLocation<any>();
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setFiltersOpen(false);
  }, [signature]);

  const baseClass = "filters-section";
  const rootClass = classNames(baseClass, className, {
    [`${baseClass}--open`]: filtersOpen,
  });

  const handleFiltersButtonClick = useCallback(() => {
    setFiltersOpen(prev => !prev);
  }, [setFiltersOpen]);

  useEffect(() => {
    if (forceOpen) {
      setFiltersOpen(true);
    }
  }, [forceOpen]);

  useEffect(() => {
    if (location?.state?.forceFiltersOpen) {
      setFiltersOpen(true);
    }
  }, [location]);

  const showFiltersButton = !(isSmallScreen && wasLastOfferVisible);

  return (
    <Flex className={rootClass} direction="column">
      <Section
        borderRadius={!isSmallScreen}
        border={isSmallScreen ? [true, false, false, false] : undefined}
        padding={isSmallScreen ? ["md", false] : ["md"]}>
        <Flex direction="row">
          <Flex
            className={`${baseClass}__main-filters`}
            grow={1}
            direction="column"
            padding={[false, "md", false, false]}
            innerMargin="md">
            {renderMainFilters()}
          </Flex>
          {showFiltersButton && (
            <FiltersButtons
              onFilterOpenClick={handleFiltersButtonClick}
              onClearFilterClick={onClearFilters}
              hideClearFilters={hideClearFilters}
              isFilterOpen={filtersOpen}
              appearance={isSmallScreen ? "mobile" : "desktop"}
            />
          )}
        </Flex>
      </Section>

      <FiltersCollapsible collapsed={!filtersOpen} appearance={isSmallScreen ? "mobile" : "desktop"}>
        {renderMoreFilters()}
      </FiltersCollapsible>

      {!filtersOpen && !!activeFilters.length && (
        <Flex
          className={`${baseClass}__badges`}
          margin={["md", false, false, false]}
          padding={isSmallScreen ? [false, "sm"] : undefined}>
          <FiltersActiveFilters activeFilters={activeFilters} onClearFilters={onClearFilters} />
        </Flex>
      )}
    </Flex>
  );
};
